
import { useState } from "react";
import { Rule } from "@/lib/types";
import { RulesList } from "@/components/rules/RulesList";
import RuleDrawer from "@/components/rules/RuleDrawer";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface RuleManagerProps {
  mailboxId: string;
  tenantId: string;
  rules: Rule[];
  isLoading: boolean;
  onRuleUpdated: (mailboxId: string) => Promise<void | boolean>;
  onReorder: (mailboxId: string, updatedRules: Rule[]) => void;
  onDeleteRule?: (rule: Rule) => Promise<boolean>; // Add this prop to receive the delete function
}

export function RuleManager({ 
  mailboxId, 
  tenantId, 
  rules, 
  isLoading, 
  onRuleUpdated, 
  onReorder,
  onDeleteRule 
}: RuleManagerProps) {
  const [ruleToEdit, setRuleToEdit] = useState<Rule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<Rule | null>(null);
  const [isAddRuleDrawerOpen, setIsAddRuleDrawerOpen] = useState(false);
  const [isEditRuleDrawerOpen, setIsEditRuleDrawerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleAddRuleClick = () => {
    setIsAddRuleDrawerOpen(true);
  };

  const handleEditRule = (rule: Rule) => {
    setRuleToEdit(rule);
    setIsEditRuleDrawerOpen(true);
  };

  const handleDeleteRule = (rule: Rule) => {
    setRuleToDelete(rule);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDeleteRule = async () => {
    if (ruleToDelete) {
      // Call the provided delete function if it exists
      if (onDeleteRule) {
        await onDeleteRule(ruleToDelete);
      }
      // Also call onRuleUpdated to refresh the rules list
      await onRuleUpdated(mailboxId);
      setRuleToDelete(null);
      setIsDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <Skeleton className="h-8 w-full mx-auto" />
        <Skeleton className="h-8 w-full mx-auto mt-2" />
      </div>
    );
  }

  return (
    <>
      <RulesList
        rules={rules || []}
        isLoading={isLoading}
        selectedMailboxId={mailboxId}
        tenantId={tenantId}
        onEdit={handleEditRule}
        onDelete={handleDeleteRule}
        onAddClick={handleAddRuleClick}
        onReorder={(updatedRules) => onReorder(mailboxId, updatedRules)}
      />

      {/* Mounted only while open, so each new rule starts from a clean form.
          Same pattern as the edit drawer below. */}
      {isAddRuleDrawerOpen && (
        <RuleDrawer
          mode="create"
          tenantId={tenantId}
          mailboxId={mailboxId}
          onSuccess={() => onRuleUpdated(mailboxId)}
          open={isAddRuleDrawerOpen}
          onOpenChange={setIsAddRuleDrawerOpen}
          triggerButton={false}
        />
      )}
      
      {ruleToEdit && (
        <RuleDrawer
          mode="edit"
          tenantId={tenantId}
          mailboxId={mailboxId}
          rule={ruleToEdit}
          onSuccess={() => {
            onRuleUpdated(mailboxId);
            setRuleToEdit(null);
          }}
          open={isEditRuleDrawerOpen}
          onOpenChange={(open) => {
            setIsEditRuleDrawerOpen(open);
            if (!open) setRuleToEdit(null);
          }}
          triggerButton={false}
        />
      )}
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the rule "{ruleToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRuleToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDeleteRule} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
